import { forwardRef, useState } from "react"
import { useFormContext } from "react-hook-form"

import { faIcon } from "@utils/faIcon"

import { InputHelpTextProps } from "@atoms/InputHelpText/InputHelpText.types"

import Input from "@molecules/Form/inputs/Input/Input"
import { InputProps } from "@molecules/Form/inputs/Input/Input.types"
import PasswordStrengthIndicator from "@molecules/Form/inputs/PasswordStrengthIndicator/PasswordStrengthIndicator"

import { PasswordInputProps } from "./PasswordInput.types"

function getPasswordValidationMessages(value: string, isFieldInError: boolean, isTouched: boolean) {
    const hasValue = value.length > 0
    const hasMinLength = value.length >= 8
    const hasLetters = /[a-zA-Z]/.test(value)
    const hasNumbers = /\d/.test(value)

    const getMessage = (passes: boolean, message: string): InputHelpTextProps => {
        if (passes && hasValue) {
            return { status: "success", showIcon: true, message } // Show green check as soon as rule passes
        }
        if (!passes && isTouched && isFieldInError) {
            return { status: "error", showIcon: true, message } // Only show red X after touch
        }
        return {
            status: "neutral",
            showIcon: true,
            neutralIcon: faIcon({ name: "circle-check", style: "regular" }),
            message,
        }
    }

    const messages: InputHelpTextProps[] = [
        getMessage(hasMinLength, "At least 8 characters"),
        getMessage(hasLetters, "Letters"),
        getMessage(hasNumbers, "Numbers"),
    ]

    return messages
}

function PasswordInput<T>(props: PasswordInputProps & InputProps<T>, ref: React.Ref<HTMLInputElement>) {
    const { isDisabled, displayStrengthIndicator = false, displayValidationMessages = true, ...rest } = props

    // Get form context to determine touched/error state
    const form = useFormContext()
    const fieldState = form?.getFieldState(props.name, form.formState)
    const isTouched: boolean = fieldState?.isTouched || form?.formState.isSubmitted || false
    const isFieldInError: boolean = !!fieldState?.error

    // Initialize from form value then track locally
    const initialFormValue = (form?.getValues(props.name) as string) ?? ""
    const [value, setValue] = useState<string>(initialFormValue)

    const passwordHelpTexts = getPasswordValidationMessages(value, isFieldInError, isTouched)

    return (
        <Input
            ref={ref}
            type="password"
            isDisabled={isDisabled}
            placeholder="Password"
            indicator={displayStrengthIndicator ? <PasswordStrengthIndicator password={value} /> : undefined}
            inputHelpText={displayValidationMessages ? passwordHelpTexts : rest.inputHelpText}
            horizontalHelpText={displayValidationMessages}
            preserveInputHelpText={displayValidationMessages}
            onChangeCapture={(event) => setValue(event.target.value)}
            showVisibilityToggle={true}
            {...rest}
            size="md"
        />
    )
}

interface PasswordInputComponent {
    <T>(props: PasswordInputProps & InputProps<T> & React.RefAttributes<HTMLInputElement>): React.ReactElement | null
    displayName?: string
}

const PasswordInputWithRef = forwardRef(PasswordInput) as unknown as PasswordInputComponent
PasswordInputWithRef.displayName = "Input"

export default PasswordInputWithRef
